import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Container, PageHeader, Spinner } from "@/components/common";
import { LevelBadge } from "@/components/LevelBadge";
import { formatTime, formatDate } from "@/lib/format";
import { Clock, MapPin, Plus, Check, X } from "lucide-react";

export default function MyClasses() {
  const qc = useQueryClient();
  const { data: mine, isLoading } = useQuery({ queryKey: ["my-classes"], queryFn: async () => (await api.get("/me/classes")).data });
  const { data: all } = useQuery({ queryKey: ["classes"], queryFn: async () => (await api.get("/classes")).data });
  const mineIds = new Set((mine || []).map((c) => c.id));

  const add = async (id) => { await api.post("/me/classes", { class_id: id }); qc.invalidateQueries({ queryKey: ["my-classes"] }); toast.success("Class added"); };
  const remove = async (id) => { await api.delete(`/me/classes/${id}`); qc.invalidateQueries({ queryKey: ["my-classes"] }); toast("Class removed"); };

  return (
    <div>
      <PageHeader eyebrow="My Classes" title="The classes you follow" subtitle="Follow the classes you attend to see their latest dances, playlists and notices first." />
      <Container className="py-8">
        {isLoading ? <Spinner /> : (
          <>
            <h2 className="font-display text-xl font-bold text-[#2D1E18]">Following</h2>
            {mine?.length ? (
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                {mine.map((c) => (
                  <div key={c.id} className="rounded-2xl border border-border bg-card p-5" data-testid={`my-class-${c.slug}`}>
                    <div className="flex items-start justify-between gap-2">
                      <Link to={`/classes/${c.slug}`} className="font-display text-lg font-bold text-[#2D1E18] hover:text-[#B04B26]">{c.name}</Link>
                      <button onClick={() => remove(c.id)} data-testid={`remove-class-${c.slug}`} aria-label="Stop following" className="rounded-lg p-1.5 text-[#6E5B51] hover:bg-secondary hover:text-[#991B1B]"><X size={18} /></button>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-[#5C4A42]">
                      <LevelBadge level={c.level} />
                      <span className="inline-flex items-center gap-1"><Clock size={13} /> {c.day} {formatTime(c.start_time)}</span>
                      <span className="inline-flex items-center gap-1"><MapPin size={13} /> {c.venue?.name}</span>
                    </div>
                    {c.history?.[0] && (
                      <div className="mt-3 rounded-lg bg-secondary p-3">
                        <p className="text-xs font-bold text-[#B04B26]">Latest: {formatDate(c.history[0].date, { weekday: "short", year: true })}</p>
                        <p className="mt-1 text-sm text-[#5C4A42]">{c.history[0].dances.map((x) => x.dance.name).join(", ")}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : <p className="mt-3 text-[#6E5B51]">You're not following any classes yet. Add some below.</p>}

            <h2 className="mt-10 font-display text-xl font-bold text-[#2D1E18]">All classes</h2>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {(all || []).map((c) => {
                const following = mineIds.has(c.id);
                return (
                  <div key={c.id} className="flex items-center justify-between gap-2 rounded-xl border border-border bg-card p-4">
                    <div>
                      <p className="font-semibold text-[#2D1E18]">{c.name}</p>
                      <p className="text-sm text-[#6E5B51]">{c.day} · {c.venue?.name}</p>
                    </div>
                    <button onClick={() => following ? remove(c.id) : add(c.id)} data-testid={`toggle-class-${c.slug}`}
                      className={`inline-flex shrink-0 items-center gap-1 rounded-full px-3 py-1.5 text-sm font-semibold ${following ? "bg-[#E8F0EC] text-[#4A6B5D]" : "bg-[#C85A32] text-white"}`}>
                      {following ? <><Check size={14} /> Following</> : <><Plus size={14} /> Follow</>}
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </Container>
    </div>
  );
}
